import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Stack,
    Alert
} from "@mui/material";
// import { sendMessageToUsers } from "path/to/signalRConnection";
import { sendMessageToUsers } from "services/signalR/signalRService";
import { addMessage } from "features/Messages/dataStore/messageSlice";

export function MessageComposer(props) {
    const { open, onClose, replyTo } = props;
    const dispatch = useDispatch();
    const currentUserId = useSelector((state) => state.message.currentUserId);

    const [toUserId, setToUserId] = useState("");
    const [messageText, setMessageText] = useState("");
    const [error, setError] = useState(null);
    const [sending, setSending] = useState(false);

    // Prefill/reset whenever the dialog opens or the reply target changes
    useEffect(() => {
        if (open) {
            setToUserId(replyTo?.userId ? String(replyTo.userId) : "");
            setMessageText("");
            setError(null);
        }
    }, [open, replyTo]);

    const isReply = Boolean(replyTo);

    const handleSend = async () => {
        if (!toUserId.trim() || !messageText.trim()) {
            setError("Please provide a user id and a message.");
            return;
        }

        const messageId = replyTo?.messageId ?? crypto.randomUUID();
        const messageType = replyTo ? "reply" : "test";

        const payload = {
            message: messageText.trim(),
            messageType,
            messageId
        };

        setSending(true);
        setError(null);

        try {
            // toUserId can be a single id for now; comma-separate later for multi-recipient
            /* const userIds = toUserId
                 .split(",")
                 .map((id) => id.trim())
                 .filter(Boolean);*/

            const rawUserIds = toUserId
                .split(",")
                .map((id) => id.trim())
                .filter(Boolean);

            const userIds = rawUserIds.map(Number);

            if (userIds.some((id) => !Number.isInteger(id) || id <= 0)) {
                setError("Please enter valid user IDs.");
                return;
            }

            await sendMessageToUsers(userIds, payload);

            // Optimistic add so it shows up in your own thread immediately
            dispatch(
                addMessage({
                    ...payload,
                    fromUserId: currentUserId,
                    toUserIds: userIds
                })
            );

            onClose();
        } catch (err) {
            console.error("Failed to send message:", { err });
            setError("Failed to send message. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isReply ? "Reply" : "New Message"}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField
                        label="To User Id"
                        value={toUserId}
                        onChange={(e) => setToUserId(e.target.value)}
                        disabled={isReply}
                        helperText={
                            isReply
                                ? "Replying within this conversation"
                                : "Comma-separate multiple ids"
                        }
                        fullWidth
                    />
                    <TextField
                        label="Message"
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={sending}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSend}
                    disabled={sending}
                >
                    Send
                </Button>
            </DialogActions>
        </Dialog>
    );
}
